import { CapacityClient } from '@api/CapacityClient';
import { useModalStack } from '@components/modal/ModalStackProvider';
import { SystemBusyModal } from '@components/modal/SystemBusyModal';
import { SkeletonLoad } from '@components/loading/SkeletonLoad';
import { ExtendedUserInformation } from '@models/User';
import ServiceUnavailablePage from '@pages/ServiceUnavailablePage';
import SystemBusyPage from '@pages/SystemBusyPage';
import { usePTVNAuthentication } from '@ptvn-react/authentication';
import { HttpStatus } from '@utils/constants/CommonConstants';
import PageRoute from '@utils/constants/PageRoute';
import { SYSTEM_BUSY_EVENT } from '@utils/helpers/CapacityHelper';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { Subscription } from 'rxjs';

type GateState = 'READY' | 'LOADING' | 'FULL' | 'ERROR';

export const CapacityProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const { userInformation } = usePTVNAuthentication<ExtendedUserInformation>();
  const location = useLocation();
  const modalStack = useModalStack();
  const [gateState, setGateState] = useState<GateState>('READY');
  const subscription = useRef<Subscription>();
  const bypassCapacityCheck = useMemo(() => [
    PageRoute.REDIRECT,
    PageRoute.LOGIN,
    PageRoute.LOGOUT,
    PageRoute.NOT_FOUND,
    PageRoute.NEW_LANDING,
    PageRoute.TERMS_AND_PRIVACY,
    PageRoute.VERSION,
    PageRoute.COMPANY_INFO,
    PageRoute.VERIFY_CHANGE_MAIL,
    PageRoute.EMERGENCY_MAINTENANCE_PAGE
  ].some((path) => !!matchPath(location.pathname, { path, exact: true })), [location.pathname]);

  const checkCapacity = useCallback(() => {
    if (!userInformation || bypassCapacityCheck) {
      setGateState('READY');
      return;
    }
    setGateState('LOADING');
    subscription.current && subscription.current.unsubscribe();
    subscription.current = CapacityClient.getCapacityStatus().subscribe(
      (capacity) => setGateState(
        capacity.mode === 'ENFORCE' && capacity.status === 'FULL' ? 'FULL' : 'READY'
      ),
      (error) => {
        const status = error?.response?.status ?? error?.status;
        setGateState(status === HttpStatus.Unauthorized ? 'READY' : 'ERROR');
      }
    );
  }, [bypassCapacityCheck, userInformation]);

  useEffect(() => {
    checkCapacity();
    return () => subscription.current && subscription.current.unsubscribe();
  }, [checkCapacity]);

  useEffect(() => {
    const showBusyModal = () => {
      modalStack.dismissAll();
      modalStack.push(SystemBusyModal, { onSubmit: () => undefined }, undefined, 'system-busy');
    };
    window.addEventListener(SYSTEM_BUSY_EVENT, showBusyModal);
    return () => window.removeEventListener(SYSTEM_BUSY_EVENT, showBusyModal);
  }, [modalStack]);

  if (gateState === 'LOADING') {
    return <SkeletonLoad />;
  }
  if (gateState === 'FULL') {
    return <SystemBusyPage onRetry={checkCapacity} />;
  }
  if (gateState === 'ERROR') {
    return <ServiceUnavailablePage />;
  }
  return <>{children}</>;
};
